'use client';
import { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { useGeofencing } from '@/hooks/useGeofencing';

interface Toast {
  id: string;
  message: string;
  type: 'enter' | 'exit' | 'temp';
}

export default function GeofencingProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const router = useRouter();

  const addToast = useCallback((message: string, type: Toast['type']) => {
    const id = Math.random().toString(36).slice(2);
    setToasts((prev) => [...prev.slice(-2), { id, message, type }]);
    setTimeout(() => setToasts((prev) => prev.filter((t) => t.id !== id)), 6000);
  }, []);

  const onZoneEntered = useCallback((zoneName: string) => {
    addToast(`📍 Entered "${zoneName}" — tracking started`, 'enter');
  }, [addToast]);

  const onZoneExited = useCallback((zoneName: string, durationMinutes: number) => {
    const h = Math.floor(durationMinutes / 60);
    const m = durationMinutes % 60;
    const dur = h > 0 ? `${h}h ${m}m` : `${m}m`;
    addToast(`✓ Left "${zoneName}" — ${dur} logged`, 'exit');
  }, [addToast]);

  const onTempZoneDetected = useCallback((lat: number, lng: number, durationMinutes: number) => {
    const h = Math.floor(durationMinutes / 60);
    const m = durationMinutes % 60;
    const dur = h > 0 ? `${h}h ${m}m` : `${m}m`;
    addToast(`⏱ ${dur} in unknown area — tap to confirm`, 'temp');
  }, [addToast]);

  useGeofencing({ onZoneEntered, onZoneExited, onTempZoneDetected });

  const toastColors = {
    enter: { bg: 'rgba(0,230,118,0.12)', border: 'rgba(0,230,118,0.25)', text: '#00e676' },
    exit: { bg: 'rgba(99,179,237,0.1)', border: 'rgba(99,179,237,0.2)', text: '#63b3ed' },
    temp: { bg: 'rgba(251,191,36,0.1)', border: 'rgba(251,191,36,0.25)', text: '#fbbf24' },
  };

  return (
    <>
      {children}
      {/* Geofencing toasts */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => {
          const c = toastColors[t.type];
          return (
            <div
              key={t.id}
              onClick={t.type === 'temp' ? () => router.push('/time-logs') : undefined}
              className="rounded-xl px-4 py-3 text-sm font-medium border max-w-xs shadow-lg"
              style={{
                background: c.bg,
                borderColor: c.border,
                color: c.text,
                backdropFilter: 'blur(12px)',
                pointerEvents: t.type === 'temp' ? 'auto' : 'none',
                cursor: t.type === 'temp' ? 'pointer' : 'default',
              }}
            >
              {t.message}
              {t.type === 'temp' && <span className="ml-2 underline text-xs">Review →</span>}
            </div>
          );
        })}
      </div>
    </>
  );
}
